import type { FillPatternEventArgs } from "@glideapps/glide-data-grid";
import { useRef, useState } from "react";
import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";
import { flushWorkbookCellEdits } from "@/payroll/ai-script/workbook-cell-edit-batcher";
import type { AiScriptSpreadsheetViewerProps } from "./ai-script-spreadsheet-viewer-types";
import { createBulkEditHandlers } from "./bulk-edit-handlers";
import { createCellEditHandlers } from "./cell-edit-handlers";
import { createColumnValueTypeChange } from "./column-value-type-change";
import { createGridPointerHandlers } from "./grid-pointer-handlers";
import { createManualRowActions } from "./manual-row-actions";
import {
  createManualRowEditBuffer,
  type ViewerEditContext,
} from "./manual-row-edit-buffer";
import { createRegionHandlers } from "./region-handlers";
import { useFormulaFillInteractions } from "./use-formula-fill-interactions";
import { useSearchNavigation } from "./use-go-to-cell";
import { useRowResize } from "./use-row-resize";
import { useUndoRedoShortcut } from "./use-viewer-shortcuts";
import type { ViewerState } from "./use-viewer-state";
import { useViewerUndoRedo } from "./use-viewer-undo-redo";
import { formatCoordinate } from "./utils";

export const useViewerActions = (
  state: ViewerState,
  {
    businessId,
    runId,
    onAskAiAboutCell,
    onNoteFromSelection,
    cellLink,
    cellSource,
    onViewEditedCell,
  }: AiScriptSpreadsheetViewerProps,
) => {
  const {
    queryClient,
    history,
    mutations,
    workbookVersionRef,
    sheet,
    ui,
    comments,
    errorHandlers: { handleMutationError, showMutationErrorFeedback },
  } = state;
  const { activeSheet, activeLoadedSheet, adaptedSheet, canMutateActiveSheet } =
    sheet;

  const retryUnsavedChangesPendingRef = useRef(false);
  const [isRetryingUnsavedChanges, setIsRetryingUnsavedChanges] =
    useState(false);

  const recordWorkbookVersion = (version: number | undefined) => {
    if (version != null) workbookVersionRef.current = version;
  };

  const findLoadedCellById = (cellId: string): IAiSpreadsheetCell | undefined =>
    activeLoadedSheet?.cells.find(
      (cell) => cell.id === cellId || cell.optimistic_cell_id === cellId,
    );

  const coordinateForCell = (cell: IAiSpreadsheetCell): string =>
    formatCoordinate([cell.col - 1, cell.row - 1]);

  const { isHistoryBlocked, undo, redo } = useViewerUndoRedo({
    businessId,
    runId,
    isLocked: state.isLocked,
    hasActiveSheetError: !!sheet.activeSheetError,
    isWorkbookOperationPending: state.workbookOperation.isPending,
    queryClient,
    history,
    mutations,
    workbookVersionRef,
    activeSheet,
    recordWorkbookVersion,
    findLoadedCellById,
    handleMutationError,
  });
  useUndoRedoShortcut({
    isViewerShortcutScopeActive: state.isViewerShortcutScopeActive,
    undo,
    redo,
  });
  useSearchNavigation({
    searchNavigationTarget: state.search.navigationTarget,
    clearGridViewport: sheet.clearGridViewport,
    gridRef: state.gridRef,
    setGridSelection: ui.setGridSelection,
  });

  const editContext: ViewerEditContext = {
    businessId,
    runId,
    queryClient,
    history,
    mutations,
    activeSheet,
    bufferedManualRowEditsRef: state.bufferedManualRowEditsRef,
    recordWorkbookVersion,
    handleMutationError,
  };
  const buffer = createManualRowEditBuffer(editContext);
  const retryUnsavedChanges = async () => {
    if (retryUnsavedChangesPendingRef.current) return;
    retryUnsavedChangesPendingRef.current = true;
    setIsRetryingUnsavedChanges(true);
    try {
      await buffer.retryFailedManualRowEdits();
    } finally {
      retryUnsavedChangesPendingRef.current = false;
      setIsRetryingUnsavedChanges(false);
    }
  };
  const rowActions = createManualRowActions({
    ...editContext,
    canMutateActiveSheet,
    maxContentRow: state.maxContentRow,
    maxContentCol: state.maxContentCol,
    clearGridViewport: sheet.clearGridViewport,
    gridRef: state.gridRef,
    setGridSelection: ui.setGridSelection,
    buffer,
  });
  const cellEdits = createCellEditHandlers({
    ...editContext,
    adaptedSheet,
    canMutateActiveSheet,
    findLoadedCellById,
    coordinateForCell,
    showMutationErrorFeedback,
    buffer,
    rowActions,
  });
  const bulkEdits = createBulkEditHandlers({
    ...editContext,
    adaptedSheet,
    canMutateActiveSheet,
    hasActiveRegionView: sheet.hasActiveRegionView,
    maxDisplayRow: state.maxDisplayRow,
    maxContentCol: state.maxContentCol,
    consumePasteMetadata: state.clipboard.consumePasteMetadata,
    buffer,
    cellEdits,
  });

  const { consumeFillCancellation } = useFormulaFillInteractions({
    enabled: canMutateActiveSheet,
    getCellRecord: adaptedSheet?.getCellRecord,
    gridSelection: ui.gridSelection,
    gridWrapperRef: state.gridWrapperRef,
    onFill: bulkEdits.commitFormulaFill,
  });

  const handleFormulaFillPattern = (event: FillPatternEventArgs) => {
    event.preventDefault();
    if (consumeFillCancellation()) return;
    bulkEdits.commitFormulaFill(event, "Fill formulas");
  };

  const pointer = createGridPointerHandlers({
    onAskAiAboutCell,
    canNoteFromSelection: !!onNoteFromSelection,
    cellLink,
    cellSource,
    canViewEditedCells: !!onViewEditedCell,
    adaptedSheet,
    activeLoadedSheet,
    regionRowMap: sheet.regionRowMap,
    maxDisplayRow: state.maxDisplayRow,
    canMutateActiveSheet,
    canEditSheetLayout: state.sheetLayout.canEdit,
    openSpreadsheetColumnMenu: state.keyboard.openSpreadsheetColumnMenu,
    comments,
    ui,
  });
  const regions = createRegionHandlers({
    runId,
    activeLoadedSheet,
    activeSheetValue: sheet.activeSheetValue,
    canMutateActiveSheet,
    handleMutationError,
    mutations,
    ui,
  });
  const columnValueType = createColumnValueTypeChange({
    runId,
    activeSheet,
    pendingWorkbookMutations: state.pendingWorkbookMutations,
    canMutateActiveSheet,
    isHistoryBlocked,
    isSavingLayout: state.sheetLayout.isSaving,
    history,
    workbookVersionRef,
    recordWorkbookVersion,
    handleMutationError,
    mutations,
  });

  const handleRowAppended = () => rowActions.appendManualRow();

  const rowResize = useRowResize({
    enabled: state.sheetLayout.canResize,
    scope: state.sheetLayout.scope,
    sheetId: activeLoadedSheet?.id ?? null,
    rowMap: sheet.regionRowMap,
    rowCount: adaptedSheet?.rowCount ?? 0,
    frozenRows: sheet.frozenRow ?? 0,
    gridRef: state.gridRef,
    getRowHeight: adaptedSheet?.getRowHeight,
    onResizeEnd: state.sheetLayout.onRowResizeEnd,
  });

  const handleSheetChange = (value: string) => {
    sheet.changeActiveSheet(value);
    if (businessId) void flushWorkbookCellEdits(businessId, runId);
  };

  return {
    isHistoryBlocked,
    undo,
    redo,
    rowActions,
    cellEdits,
    bulkEdits,
    isRetryingUnsavedChanges,
    retryUnsavedChanges: canMutateActiveSheet ? retryUnsavedChanges : undefined,
    handleFormulaFillPattern,
    pointer,
    regions,
    columnValueType,
    handleRowAppended,
    rowResize,
    handleSheetChange,
  };
};
